import { Button, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { number, object, string } from 'yup';
import { useCreateDesign } from '~/api/designs';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent, DialogForm } from '~/components/dialogs/lib';
import { NumberPairField } from '~/components/forms/number-pair';
import { useAppContext } from '~/contexts';

export interface StudioNewDesignDialogProps {
  onClose: () => void;
  open: boolean;
}

export const StudioNewDesignDialog = ({ onClose, open }: StudioNewDesignDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [createDesign] = useCreateDesign();
  const { designFolderId } = useParams<{ designFolderId?: string }>();
  const folderId = designFolderId ? Number(designFolderId) : undefined;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      height: 1080,
      name: '',
      width: 1920,
    },
    onSubmit: async (values) => {
      const { data } = await createDesign({
        variables: {
          input: { ...values, networkId: currentNetwork.id, designFolderId: folderId },
        },
      });
      if (data?.createDesign?.design) {
        navigate({ pathname: `design/${data.createDesign.design.id}`, search: location.search });
      }
    },
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: object({
      height: number()
        .required(t('common.labels.height_required'))
        .min(0, t('common.labels.height_min'))
        .label(t('common.labels.height')),
      name: string().required(t('common.labels.name_required')).label(t('common.labels.name')),
      width: number()
        .required(t('common.labels.width_required'))
        .min(0, t('common.labels.width_min'))
        .label(t('common.labels.width')),
    }),
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited: () => formik.resetForm() }}
    >
      <DialogTitle onClose={onClose}>{t('pages.studio.dialogs.new_design.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>{t('pages.studio.dialogs.new_design.description')}</DialogContentTitle>

        <DialogForm id="new-design-form" onSubmit={formik.handleSubmit}>
          <label>
            <span className={`label required`}>{t('common.labels.name')}</span>
            <TextField
              error={formik.touched.name && !!formik.errors.name}
              fullWidth
              helperText={(formik.touched.name && formik.errors.name) || ' '}
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
              focused
              autoFocus
            />
          </label>
          <NumberPairField
            fields={[
              { label: t('common.labels.width'), name: 'width', sx: { mr: 1 } },
              { label: t('common.labels.height'), name: 'height', sx: { ml: 1 } },
            ]}
            formik={formik}
            label={t('common.labels.size')}
            required
            separator={<span>x</span>}
          />
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={onClose} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          form="new-design-form"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          type="submit"
          variant="contained"
        >
          {t('common.dialogs.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
